import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PolicyLine } from "@/components/admin/fields/policy-lines/types";

// Define a simple type for the database response
type DatabasePolicyLine = {
  id: string;
  name: string;
  description: string | null;
  order: number | null;
};

export const usePolicyLines = (policyTypeId?: string) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<PolicyLine[]>({
    queryKey: ["policyLines", policyTypeId],
    queryFn: async () => {
      const { data: policyLines, error } = await supabase
        .from("policy_lines_new")
        .select("id, name, description, order")
        .returns<DatabasePolicyLine[]>();

      if (error) throw error;
      return policyLines || [];
    },
  });

  const updateOrder = useMutation({
    mutationFn: async (policyLines: PolicyLine[]) => {
      const updates = policyLines.map((line) => ({
        id: line.id,
        name: line.name,
        description: line.description,
        order: line.order,
      }));

      const { error } = await supabase
        .from("policy_lines_new")
        .upsert(updates, { onConflict: "id" });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policyLines"] });
      toast.success("Policy line order updated successfully");
    },
    onError: (error) => {
      console.error("Error updating policy line order:", error);
      toast.error("Failed to update policy line order");
    },
  });

  return {
    data,
    isLoading,
    updateOrder,
  };
};